/**
 * runComposition — shared helpers for classifying a BacktestSummary as a
 * single-asset run vs. a portfolio (aggregation) run, and for deriving the
 * number of sub-assets involved.
 *
 * Discriminator (Phase 3.5): a run is single-asset IFF it has a
 * `strategyConfigId` (matches server bucketing). Everything else — genuine
 * aggregations AND legacy `symbol='MULTI'` runs with no config link — is a
 * portfolio/aggregation run.
 *
 * `strategyVersions` is a per-run provenance map keyed by strategy name
 * (populated for both single and aggregation runs). For an aggregation run,
 * its key count is a reliable proxy for the number of sub-strategies/assets
 * involved (aggregate-of-one vs. genuine multi-asset portfolio) — the same
 * convention already used by PortfoliosPage's RecentRunsTable.
 */

import type { BacktestSummary } from '../types';

/** True when `run` is an aggregation/portfolio run (not a single-asset run). */
export function isAggregationRun(run: BacktestSummary): boolean {
  return !!run.aggregationId || !!run.aggregationName || !run.strategyConfigId;
}

/**
 * Number of sub-strategies/assets in an aggregation run, derived from the
 * `strategyVersions` provenance map. Returns `undefined` when the run has no
 * `strategyVersions` (older runs predating that field) — callers should treat
 * `undefined` as "unknown count", not "single asset".
 */
export function getSubAssetCount(run: BacktestSummary): number | undefined {
  if (!run.strategyVersions) return undefined;
  const count = Object.keys(run.strategyVersions).length;
  return count > 0 ? count : undefined;
}

/** True when `run` is an aggregation run composed of exactly one sub-strategy. */
export function isAggregationOfOne(run: BacktestSummary): boolean {
  return isAggregationRun(run) && getSubAssetCount(run) === 1;
}

/**
 * Human-readable composition label for a run:
 *   - single-asset run          → the traded symbol (e.g. "BTC/USDT")
 *   - aggregation-of-one        → "1 asset" (NOT "Portfolio" — it's not a multi-asset portfolio)
 *   - genuine multi-asset agg   → "Portfolio"
 *   - legacy `symbol==='MULTI'` with no strategyVersions (unknown count) → "Portfolio"
 */
export function getCompositionLabel(run: BacktestSummary): string {
  if (!isAggregationRun(run)) return run.symbol;
  return isAggregationOfOne(run) ? '1 asset' : 'Portfolio';
}
